// Per-route <head> metadata shared by the prerenderer (static HTML) and the client (SPA navigation).
import { PRODUCTS, getProduct } from "./data/products";
import { SOLUTIONS } from "./data/solutions";

export const SITE_ORIGIN = "https://actrium.ai";
export const OG_IMAGE = `${SITE_ORIGIN}/assets/images/og-image.png`;
const DEFAULT_DESCRIPTION =
  "Actrium connects human demonstrations, multimodal capture, robot learning systems and traceable data delivery.";

/** Static routes: title, description, and whether they belong in the sitemap. */
const STATIC_ROUTES = {
  "/": {
    title: "Robot Learning & Data Infrastructure",
    description:
      "Actrium AI builds hardware and data infrastructure for robot learning: multimodal capture, robot learning workstations, data operations and training compute for embodied AI teams.",
  },
  "/products": {
    title: "Products",
    description: "Explore Actrium capture devices, tactile sensing and Atlas robot learning platforms.",
  },
  "/products/thor-ego": {
    title: "Ego Thor Series",
    description: "Compare Thor-Duo, Thor-Quad and Thor-Hex visual-inertial capture systems.",
  },
  "/solutions": {
    title: "Solutions",
    description:
      "Three starting points for data production, robot experiments and multimodal research. Define the outcome, then select the hardware, software and services.",
  },
  "/solutions/capture/data-platform": {
    title: "Data Platform",
    description:
      "Organize multimodal capture, quality review, dataset versions and traceable batch delivery.",
  },
  "/training": {
    title: "Training & Compute",
    description:
      "Train, fine-tune and deploy robot learning models with Actrium: project-scoped GPU compute, hosted inference with local robot control and U.S. data residency. Our own U.S. GPU data center is under construction.",
  },
  "/resources/integration": {
    title: "Integration & Documentation",
    description:
      "Plan device, data, algorithm and robot runtime integration with explicit interfaces and versioned validation.",
  },
  "/about": {
    title: "About",
    description:
      "Actrium AI is a U.S.-based company operated by Impact Frontiers Inc., building Physical AI data infrastructure for robotics teams, labs and enterprise partners.",
  },
  "/careers": {
    title: "Careers",
    description:
      "Join Actrium AI to build multimodal robot learning data, evaluation and deployment workflows.",
  },
  "/legal-entity": {
    title: "Legal Entity",
    description:
      "Legal registration details for Impact Frontiers Inc., the company operating the Actrium AI brand.",
  },
  "/privacy": {
    title: "Privacy Policy",
    description: "How Actrium AI collects, uses and protects information.",
  },
  "/terms": {
    title: "Terms of Service",
    description: "Terms governing use of Actrium AI websites, data infrastructure and services.",
  },
};

/** Placeholder product pages that exist on the live site but have no content yet (kept, but not indexed). */
export const COMING_SOON_ROUTES = {
  "/products/egoforce-kit": "THOR Capture Stack",
  "/products/forcealign": "EGO-to-Robot",
  "/products/forcebench": "AEgo Edge Hub",
  "/products/actrium-studio": "AEgo Engine",
};

export function normalizePath(pathname) {
  return pathname.replace(/\/+$/, "") || "/";
}

/** Returns { title, description, canonical, noindex, status } for a path. status 404 = unknown route. */
export function getRouteMeta(pathname) {
  const path = normalizePath(pathname);
  const canonical = `${SITE_ORIGIN}${path === "/" ? "/" : path}`;
  const make = (name, description, extra = {}) => ({
    title: `${name} | Actrium AI`,
    description: description || DEFAULT_DESCRIPTION,
    canonical,
    noindex: false,
    status: 200,
    ...extra,
  });
  if (STATIC_ROUTES[path]) return make(STATIC_ROUTES[path].title, STATIC_ROUTES[path].description);
  if (COMING_SOON_ROUTES[path])
    return make(COMING_SOON_ROUTES[path], "This Actrium product page is under development.", {
      noindex: true,
    });
  const [, section, slug, rest] = path.split("/");
  if (section === "products" && slug && !rest) {
    const product = getProduct(slug);
    if (product) return make(product.name, product.intro);
  }
  if (section === "solutions" && slug && !rest) {
    const solution = SOLUTIONS.find((s) => s.slug === slug);
    if (solution) return make(solution.name, solution.intro);
  }
  return {
    ...make("Page not found", "This page is no longer available."),
    noindex: true,
    status: 404,
    canonical: null,
  };
}

/** Every indexable route, used for prerendering and sitemap.xml. */
export function indexableRoutes() {
  return [
    ...Object.keys(STATIC_ROUTES),
    ...PRODUCTS.map((p) => `/products/${p.slug}`),
    ...SOLUTIONS.map((s) => `/solutions/${s.slug}`),
  ];
}

/** Apply metadata to the live document during client-side navigation. */
export function applyRouteMeta(meta) {
  document.title = meta.title;
  const set = (selector, attr, value) => {
    const el = document.querySelector(selector);
    if (el && value != null) el.setAttribute(attr, value);
  };
  set('meta[name="description"]', "content", meta.description);
  set('link[rel="canonical"]', "href", meta.canonical);
  set('meta[property="og:title"]', "content", meta.title);
  set('meta[property="og:description"]', "content", meta.description);
  set('meta[property="og:url"]', "content", meta.canonical);
  set('meta[name="twitter:title"]', "content", meta.title);
  set('meta[name="twitter:description"]', "content", meta.description);
  set('meta[name="robots"]', "content", meta.noindex ? "noindex, follow" : "index, follow");
}
